import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SortableHeader } from './SortableHeader.js';
import type { SortDir } from './SortableHeader.js';
import { useKpiReleases } from '../../../hooks/index.js';
import { formatPt } from '../../../lib/format.js';

/**
 * 列幅の定義（`<colgroup>` 用）。`table-fixed` と組み合わせる。
 * auto layout では課題名の本文が列幅を決めてしまい、実測で Summary 列だけが 1032px を
 * 占めて他列が詰まっていた。各幅はヘッダー文字列の実測 intrinsic 幅以上にしてある
 * （ヘッダーは `whitespace-nowrap` なので幅不足だと折り返さず切れる）。
 * @graph-connects none
 */
const RELEASE_COLUMN_WIDTHS = [
  150, // リリース日 / Released Date
  152, // 課題キー（AIRCLOSET-150158 が収まる幅）
  420, // 課題名（本文は line-clamp-2 で 2 行に抑える）
  88, // ポイント / Points
  130, // 発見者 / Found By
  120, // 担当者 / Assignee
] as const;

/** colgroup の合計幅。表の min-width に使う @graph-connects none */
const RELEASE_TABLE_MIN_WIDTH = RELEASE_COLUMN_WIDTHS.reduce((sum, w) => sum + w, 0);

/**
 * 列ヘッダー共通クラス。`whitespace-nowrap` で「リリース日」「発見者」等の見出しが
 * 幅の揺れで途中改行するのを防ぐ。幅は `RELEASE_COLUMN_WIDTHS`（colgroup）で一元管理する。
 * @graph-connects none
 */
const HEAD_CLASS =
  'text-left py-2 px-3 font-medium text-[var(--color-text-muted)] whitespace-nowrap align-bottom';

type ReleaseSortKey =
  | 'releasedDate'
  | 'issueKey'
  | 'summary'
  | 'effectivePoint'
  | 'foundBy'
  | 'assignee';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business リリース詳細テーブル。リリース日・issueKey・課題名・有効ポイント・発見者・担当者を6列で表示。全列ヘッダーソート対応。発見者は bug 種別（案件起因バグ / 潜在バグ）の行のみ値が入り、プロダクト一覧の行は '-' になる
 * @graph-connects none
 */
export function ReleaseTable({
  data,
}: {
  data: NonNullable<ReturnType<typeof useKpiReleases>['data']>['data'];
}) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<ReleaseSortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const sortedData = useMemo(() => {
    if (sortKey === null) return data;
    const key = sortKey;
    return [...data].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), 'ja');
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortKey, sortDir]);

  const handleSort = (key: ReleaseSortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  if (data.length === 0) {
    return (
      <div className="text-center py-12 text-[var(--color-text-muted)]">
        {t('kpi.detail.noItems')}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table
        className="w-full text-sm table-fixed"
        style={{ minWidth: `${String(RELEASE_TABLE_MIN_WIDTH)}px` }}
      >
        <colgroup>
          {RELEASE_COLUMN_WIDTHS.map((width, index) => (
            <col key={index} style={{ width: `${String(width)}px` }} />
          ))}
        </colgroup>
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            <th className={HEAD_CLASS}>
              <SortableHeader
                label={t('kpi.detail.releasedDate')}
                sortKey="releasedDate"
                currentKey={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
              />
            </th>
            <th className={HEAD_CLASS}>
              <SortableHeader
                label={t('kpi.detail.issueKey')}
                sortKey="issueKey"
                currentKey={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
              />
            </th>
            <th className={HEAD_CLASS}>
              <SortableHeader
                label={t('kpi.detail.summary')}
                sortKey="summary"
                currentKey={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
              />
            </th>
            <th className={HEAD_CLASS}>
              <div className="flex justify-end">
                <SortableHeader
                  label={t('kpi.detail.effectivePoint')}
                  sortKey="effectivePoint"
                  currentKey={sortKey}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
              </div>
            </th>
            <th className={HEAD_CLASS}>
              <SortableHeader
                label={t('kpi.detail.foundBy')}
                sortKey="foundBy"
                currentKey={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
              />
            </th>
            <th className={HEAD_CLASS}>
              <SortableHeader
                label={t('kpi.detail.assignee')}
                sortKey="assignee"
                currentKey={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((release, index) => (
            <tr
              key={`${release.issueKey}-${String(index)}`}
              className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)]"
            >
              <td className="py-2 px-3 text-[var(--color-text-primary)] whitespace-nowrap">
                {release.releasedDate}
              </td>
              <td className="py-2 px-3">
                <a
                  href={`https://air-closet.backlog.jp/view/${release.issueKey}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--color-accent-primary)] hover:underline"
                >
                  {release.issueKey}
                </a>
              </td>
              <td className="py-2 px-3 text-[var(--color-text-primary)]">
                <div className="line-clamp-2">{release.summary}</div>
              </td>
              <td className="py-2 px-3 text-[var(--color-text-primary)] text-right">
                {formatPt(release.effectivePoint)}pt
              </td>
              <td className="py-2 px-3 text-[var(--color-text-secondary)] whitespace-nowrap">
                {release.foundBy ?? '-'}
              </td>
              <td className="py-2 px-3 text-[var(--color-text-secondary)] whitespace-nowrap">
                {release.assignee ?? '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
